'use client';

import { LandingHeader } from '@/components/landing/header';
import { LandingBreadcrumbs } from '@/components/landing/breadcrumbs';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

export default function BugReportPage() {
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newTicketId = `#RUD-${Math.floor(Math.random() * 10000)}`;
    setTicketId(newTicketId);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <>
        <main>
          <section className="bg-background py-12 md:py-16 px-4">
            <div className="max-w-2xl mx-auto">
              <LandingBreadcrumbs
                items={[
                  { label: 'Help', href: '/help' },
                  { label: 'Bug Report' },
                ]}
              />

              <div className="border border-border rounded-lg p-12 text-center bg-card">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-500/15 rounded-full mb-6">
                  <svg
                    className="w-8 h-8 text-green-600 dark:text-green-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <h1 className="text-3xl font-bold mb-2">
                  Ticket Submitted Successfully
                </h1>
                <p className="text-muted-foreground mb-6">
                  ID:{' '}
                  <span className="font-mono text-foreground">{ticketId}</span>
                </p>
                <p className="text-muted-foreground mb-8">
                  Our engineering team has received your report.
                  <br />
                  We typically respond within 24 hours.
                </p>
                <Button onClick={() => (window.location.href = '/')}>
                  Return to Homepage
                </Button>
              </div>
            </div>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      <LandingHeader />
      <main>
        <section className="bg-background py-12 md:py-16 px-4">
          <div className="max-w-2xl mx-auto">
            <LandingBreadcrumbs
              items={[
                { label: 'Help', href: '/help' },
                { label: 'Bug Report' },
              ]}
            />

            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Report an <span className="text-primary">issue</span>
              </h1>
              <p className="text-muted-foreground">
                Help us improve Rudrise. Please provide as much detail as
                possible so our team can investigate.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="border border-border rounded-lg p-8 bg-card"
            >
              {/* Email Field */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  email*
                </label>
                <input
                  type="email"
                  placeholder="natasha.vance@rudrise.com"
                  required
                  className="w-full border border-input dark:border-white/20 bg-transparent text-foreground placeholder:text-muted-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Issue Title */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Issue Title
                </label>
                <input
                  type="text"
                  placeholder="Brief summary (e.g., PDF fails to compile with Tool)"
                  required
                  className="w-full border border-input dark:border-white/20 bg-transparent text-foreground placeholder:text-muted-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Platform/OS */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Platform / OS
                </label>
                <select
                  required
                  className="w-full border border-input dark:border-white/20 bg-transparent text-foreground placeholder:text-muted-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select Platform / OS</option>
                  <option>Windows 10</option>
                  <option>Windows 11</option>
                  <option>macOS</option>
                  <option>Linux</option>
                  <option>iOS</option>
                  <option>Android</option>
                </select>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Description
                </label>
                <textarea
                  placeholder="What happened? Please provide steps to reproduce the issue."
                  required
                  rows={6}
                  className="w-full border border-input dark:border-white/20 bg-transparent text-foreground placeholder:text-muted-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">
                  Attachment
                </label>
                <div className="border-2 border-dashed border-blue-300 dark:border-blue-800/60 rounded-lg p-8 text-center bg-blue-50 dark:bg-blue-950/30">
                  <svg
                    className="w-12 h-12 text-primary mx-auto mb-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  <p className="text-sm text-muted-foreground">
                    Supported formats: jpg, png, zip, .tex (Max 20MB)
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Drag and drop or{' '}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                    >
                      Browse files
                    </button>
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full">
                Submit Bug Report
              </Button>
            </form>
          </div>
        </section>
      </main>
    </>
  );
}
